if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        
        // Check if any items have subscription selling plans
        const hasSellingPlans = Object.keys(items).some(variantId => {
          const element = document.querySelector(`[data-index="${variantId}"]`);
          return element?.dataset.sellingPlan;
        });

        if (hasSellingPlans) {
          // Use cart add API for subscription products
          this.addSubscriptionItems(items);
        } else {
          // Use cart update API for regular products
          const body = JSON.stringify({
            updates: items,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: this.getSectionsUrl(),
          });

          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
            .then((response) => {
              return response.text();
            })
            .then((state) => {
              const parsedState = JSON.parse(state);
              this.renderSections(parsedState, ids);
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
            })
            .catch(() => {
              // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
              // e.target.setCustomValidity(error);
              // e.target.reportValidity();
              // this.resetQuantityInput(ids[index]);
              // this.selectProgressBar().classList.add('hidden');
              // e.target.select();
              // this.cleanErrorMessageOnType(e);
            })
            .finally(() => {
              this.selectProgressBar().classList.add('hidden');
              this.setRequestStarted(false);
            });
        }
      }

      addSubscriptionItems(items) {
        // For subscription items, we need to handle quantity updates more carefully
        // since /cart/add adds to existing quantities rather than setting them
        
        // First, get current cart to check existing quantities
        fetch(`${routes.cart_url}.js`)
          .then(response => response.json())
          .then(cart => {
            const updates = {};
            const newItems = [];
            
            Object.keys(items).forEach(variantId => {
              const newQuantity = items[variantId];
              const element = document.querySelector(`[data-index="${variantId}"]`);
              const sellingPlan = element?.dataset.sellingPlan;
              
              // Find existing cart item with same variant and selling plan
              const existingItem = cart.items.find(item => 
                item.variant_id == variantId && 
                item.selling_plan_allocation?.selling_plan?.id == sellingPlan
              );
              
              if (existingItem) {
                // Update existing subscription item
                updates[existingItem.key] = newQuantity;
              } else if (newQuantity > 0) {
                // Add new subscription item
                const item = {
                  id: parseInt(variantId),
                  quantity: newQuantity
                };
                
                if (sellingPlan) {
                  item.selling_plan = parseInt(sellingPlan);
                }
                
                newItems.push(item);
              }
            });
            
            // Handle updates and additions
            const promises = [];
            
            // Update existing items
            if (Object.keys(updates).length > 0) {
              const updateBody = JSON.stringify({
                updates: updates,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: this.getSectionsUrl(),
              });
              
              promises.push(
                fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body: updateBody } })
                  .then(response => response.text())
              );
            }
            
            // Add new items
            if (newItems.length > 0) {
              const addBody = JSON.stringify({
                items: newItems,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: this.getSectionsUrl(),
              });
              
              promises.push(
                fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body: addBody } })
                  .then(response => response.text())
              );
            }
            
            // Execute all promises
            if (promises.length > 0) {
              return Promise.all(promises);
            } else {
              // No changes needed, just return current state
              return [JSON.stringify(cart)];
            }
          })
          .then((responses) => {
            // Parse the last response (most recent cart state)
            const lastResponse = responses[responses.length - 1];
            const parsedState = JSON.parse(lastResponse);
            this.renderSections(parsedState, Object.keys(items));
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((error) => {
            console.error('Error updating subscription items:', error);
            // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
            // e.target.setCustomValidity(error);
            // e.target.reportValidity();
            // this.resetQuantityInput(ids[index]);
            // this.selectProgressBar().classList.add('hidden');
            // e.target.select();
            // this.cleanErrorMessageOnType(e);
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
